import React from 'react';
import Immutable, { List } from 'immutable';
import { useTranslation } from 'react-i18next';

import {
  Paragraph,
} from '@uncinc/react-drupal-core';

import {
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';

import CombinedHighlightedEntities from '../../../../components/CombinedHighlightedEntities';
import format from '../../../../helpers/date';

import {
  MUNICIPALITY_AND_PROFESSIONALS_TERM_ID, COLUMN_TERM_ID,
} from '../../../../helpers';

import { FALLBACK_IMAGE } from '../../../../config/constants';

import {
  Breadcrumbs,
  FadeWrapper,
  Hero,
  MediaPlayerSuspense,
  ShareLinks,
  WysiwygField,
} from '../../../../components';

import './style.scss';

const MUNICIPALITY_REFERRAL_LINKS = {
  nl: '/nl/wat-wij-doen/voor-gemeenten',
  en: '/en/professionals',
};

const ENTITIES_VIEW_DISPLAY = 'latest_articles';

const EXTERNAL_VIDEO_DOMAINS_YOUTUBE = ['youtube.com', 'youtu.be', 'youtube'];
const EXTERNAL_VIDEO_DOMAIN_VIMEO = 'vimeo';
const EXTERNAL_VIDEO_TYPE_YOUTUBE = 'video/youtube';
const EXTERNAL_VIDEO_TYPE_VIMEO = 'video/vimeo';

const NodeArticleDetail = ({ entity, className }) => {
  const { t } = useTranslation();
  const nid = entity.getIn(['nid', 0, 'value']);
  const title = entity.getIn(['title', 0, 'value']);
  const langcode = entity.getIn(['langcode', 0, 'value']);
  const body = entity.getIn(['body', 0, 'processed'], '');
  const summary = entity.getIn(['body', 0, 'summary'], '');
  const readTimeInMinutes = entity.getIn(['node_read_time', 0, 'value'], null);
  const relatedEntities = entity.getIn(['field_related_articles'], Immutable.List()).map((entry) => entry.get('entity'));
  const subjectIds = entity.getIn(['field_subjects'], Immutable.List()).map((entry) => entry.get('target_id', null)).toJS();
  const date = entity.getIn(['field_publication_date', 0, 'value'], entity.getIn(['created', 0, 'value']));
  const articleType = entity.getIn(['field_article_type', 0, 'entity', 'name', 0, 'value'], '');
  const articleTypeId = entity.getIn(['field_article_type', 0, 'target_id'], null);
  const image = entity.getIn(
    ['field_media_item', 0],
    FALLBACK_IMAGE,
  );
  const imageTitle = entity.getIn(['field_media_item', 0, 'entity', 'field_caption', 0, 'value'], '');
  const video = entity.getIn(['field_hero_video', 0], Immutable.Map());

  const videoSource = video.getIn(['entity', 'field_media_video_file', 0, 'source'], video.getIn(['entity', 'field_media_oembed_video', 0, 'value'], ''));
  const videoPoster = video.getIn(['entity', 'field_media_poster', 0, 'url'], video.getIn(['entity', 'field_poster', 0, 'url']), typeof (image) === 'string' ? image : image.getIn(['styles', 'crop_16_9']));
  const videoExternalType = EXTERNAL_VIDEO_DOMAINS_YOUTUBE.filter((domain) => videoSource.includes(domain)).length > 0
    ? EXTERNAL_VIDEO_TYPE_YOUTUBE
    : videoSource.includes(EXTERNAL_VIDEO_DOMAIN_VIMEO)
      ? EXTERNAL_VIDEO_TYPE_VIMEO
      : 'video/mp4';

  const videoType = video.getIn(['mime_type'], videoExternalType);
  const videoCaption = video.getIn(['entity', 'field_caption', 0, 'value']);

  const locationPath = entity.getIn(['field_location', 0, 'url'], null);
  const locationTitle = entity.getIn(['field_location', 0, 'entity', 'title', 0, 'value'], null);
  const locationMunicipalityCheck = entity.getIn(['field_location', 0, 'entity', 'field_municipality', 0, 'entity', 'field_has_municipality_page', 0, 'value'], null);
  const locationMunicipalityPath = entity.getIn(['field_location', 0, 'entity', 'field_municipality', 0, 'entity', 'paths', 'nl'], null);
  const locationMunicipalityName = entity.getIn(['field_location', 0, 'entity', 'field_municipality', 0, 'entity', 'name', 0, 'value'], null);

  let articleBreadcrumb;
  if (articleTypeId === MUNICIPALITY_AND_PROFESSIONALS_TERM_ID) {
    // if artikel type: municipality add proper url
    articleBreadcrumb = { url: MUNICIPALITY_REFERRAL_LINKS[langcode], title: articleType };
  } else if (articleTypeId === COLUMN_TERM_ID) {
    // if artikel type: column show news page with type column selected
    articleBreadcrumb = { url: `/nl/artikelen?type=${COLUMN_TERM_ID}`, title: articleType };
  } else if (locationMunicipalityCheck) {
    // if article has municipality page show it
    articleBreadcrumb = { url: locationMunicipalityPath, title: locationMunicipalityName };
  } else if (locationPath) {
    articleBreadcrumb = { url: locationPath, title: locationTitle };
  } else {
    articleBreadcrumb = { url: `/nl/artikelen?type=${articleTypeId}`, title: articleType };
  }

  return (
    <article className="NodeArticleDetail">
      <Container>
        <div className="row">
          <div className="col-12">
            <Breadcrumbs
              data={[
                articleBreadcrumb,
                { title: title },
              ]}
            />
          </div>
        </div>
      </Container>

      {/* Start of Preface */}
      <Container>
        <div className="row">
          <div className="col-12 col-md-10 offset-md-1">
            <div className="Preface">
              <FadeWrapper>
                <>
                  <div className="Preface__title">
                    <h1>{title}</h1>
                  </div>
                  <div className="Preface__content">
                    <div>
                      <span>{articleType}, </span>
                      <span className="Preface__content__light">{format((new Date(date)), 'd MMMM y')}</span>
                    </div>
                    {readTimeInMinutes && (
                      <div>
                        <span>{t('general.read_time')}, </span>
                        <span className="Preface__content__light">{readTimeInMinutes} {t('general.minutes_short')}.</span>
                      </div>
                    )}
                  </div>
                </>
              </FadeWrapper>
              <FadeWrapper delay={100}>
                <div className="Preface__body">
                  <WysiwygField
                    property="schema:text"
                    html={summary}
                  />
                </div>
              </FadeWrapper>
            </div>
          </div>
        </div>
      </Container>
      {/* End of Preface */}

      {/* Start of Hero */}
      {(image && (typeof (image) !== 'string') && !videoSource) && (
        <Container>
          <Hero
            image={image}
            description={imageTitle}
          />
        </Container>
      )}
      {videoSource && (
        <Container>
          <div className="row">
            <div className="col-12">
              <div className="NodeArticleDetail__mediaplayer">
                <MediaPlayerSuspense
                  src={videoSource}
                  type={videoType}
                  controls={['play', 'volume', 'currentTime', 'seekbar', 'fullscreen']}
                  posterUrl={videoPoster}
                />
                {videoCaption && (
                  <div className="NodeArticleDetail__mediaplayer__caption">
                    {videoCaption}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      )}
      {/* End of Hero */}

      {/* Start of Body */}
      {body && (
        <Container small>
          <FadeWrapper delay={300}>
            <WysiwygField
              property="schema:text"
              className="NodeArticleDetail__body"
              html={body}
            />
          </FadeWrapper>
        </Container>
      )}
      {/* End of Body */}

      <Container>
        <FadeWrapper delay={300}>
          <div className="row">
            <div className="col-12">
              <ShareLinks />
            </div>
          </div>
        </FadeWrapper>
      </Container>

      {/* Start of paragraphs */}
      {entity.get('field_paragraphs') ? (
        <div className="node--page--paragraphs-wrapper">
          {entity.get('field_paragraphs', List()).map((paragraph) => {
            const id = paragraph.get('target_id');
            if (paragraph.getIn(['entity', 'type', 0, 'target_id']) !== 'more_info') {
              return (
                <FadeWrapper key={`paragraph:${id}`}>
                  <Paragraph
                    id={id}
                    entity={paragraph.get('entity')}
                    nid={nid}
                  />
                </FadeWrapper>
              );
            }
          })}
          {/* Force the more_info paragraph to be at the bottom */}
          {entity.get('field_paragraphs', List()).map((paragraph) => {
            const id = paragraph.get('target_id');
            if (paragraph.getIn(['entity', 'type', 0, 'target_id']) === 'more_info') {
              return (
                <FadeWrapper key={`paragraph:${id}`}>
                  <Paragraph
                    id={id}
                    entity={paragraph.get('entity')}
                  />
                </FadeWrapper>
              );
            }
          })}
        </div>
      ) : null}
      {/* End of paragraphs */}

      {entity.get('field_paragraphs', List()).size > 0 && (
        <Container>
          <FadeWrapper delay={300}>
            <ShareLinks />
          </FadeWrapper>
        </Container>
      )}

      {/* Related articles */}
      {/* Don't show related articles if there is a Selected Articles paragraph */}
      {!entity.get('field_paragraphs', List()).filter(paragraph => paragraph.getIn(['entity', 'type', 0, 'target_id']) === 'selected_articles').size && (
        <FadeWrapper delay={300}>
          <CombinedHighlightedEntities
            contentProps={{
              type: 'articles',
              link: '/nl/artikelen',
              title: t('general.readmore'),
            }}
            selectedEntities={relatedEntities}
            viewDisplay={ENTITIES_VIEW_DISPLAY}
            viewFilters={{
              field_article_type_target_id: [articleTypeId],
              field_subjects_target_id: subjectIds,
            }}
            viewArgs={[nid]}
            key="combined_highlighted_article_detail"
          />
        </FadeWrapper>
      )}
      {/* Related articles */}

    </article>
  );
};

export default NodeArticleDetail;
